/* ================================================================
   StatisticsNiveauPage.tsx — Statistiques par niveau
   Séquence / Trimestre / Annuelle — Module Statistiques & Rapports
   Accès : super_admin, admin, censeur (classes attribuées uniquement)
   ================================================================ */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3, Printer, Loader2, GraduationCap, Calendar, Search, Users, AlertTriangle
} from 'lucide-react';
import { apiService } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';

const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object') {
    for (const k of ['items', 'data', 'results', 'rows']) {
      if (Array.isArray(res[k])) return res[k];
      if (res[k] && typeof res[k] === 'object') {
        for (const k2 of ['items', 'data', 'results']) {
          if (Array.isArray(res[k][k2])) return res[k][k2];
        }
      }
    }
  }
  return [];
};

const getNum = (v: any): number => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id';
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label';

const getYearId = (): number | null => {
  try { const v = localStorage.getItem(SELECTED_YEAR_ID_KEY); return v ? Number(v) : null; } catch { return null; }
};
const getYearLabel = (): string => { try { return localStorage.getItem(SELECTED_YEAR_LABEL_KEY) || ''; } catch { return ''; } };

const fmtPct = (admis: number, evalues: number): string =>
  evalues > 0 ? ((admis / evalues) * 100).toFixed(1) + '%' : '—';

interface ClassRow {
  class_id: number;
  class_name: string;
  sexe: string;          // 'F' | 'M' | 'TOTAL'
  inscrits: number;
  evalues: number;
  admis: number;
  redoublants: number;
  exclus: number;
  convocations: number;  // élèves à convoquer (mode période)
  taux: string;
}

interface ConvokedStudent {
  student_id: number;
  class_name: string;
  name: string;
  matricule: string;
  moyenne: number | null;
  heures_absence: number;
  parent_contact: string;
  motif: string;
}

/** Élève évalué (moyenne connue) — alimente les listes admis/échoués/top5 */
interface RankedStudent {
  student_id: number;
  class_name: string;
  name: string;
  matricule: string;
  sexe: string;
  age: number | null;
  moyenne: number;
  admis: boolean;
}

const ageOf = (dob?: string): number | null => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
};

export default function StatisticsNiveauPage() {
  const { user, role: storeRole, roleName: storeRoleName } = useAuthStore();
  // Le rôle peut être exposé au niveau racine du store (role/roleName) ou sur
  // l'objet user (role/role_name) selon les versions de l'authStore.
  const role = (storeRole || storeRoleName || (user as any)?.role || (user as any)?.role_name || '').toLowerCase();
  const canAccess = ['super_admin', 'admin', 'censeur'].includes(role);
  const isCenseur = role === 'censeur';

  const [mode, setMode] = useState<'sequence' | 'trimestre' | 'annuelle'>('trimestre');
  const [levels, setLevels] = useState<any[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);
  const [sequences, setSequences] = useState<any[]>([]);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedSequence, setSelectedSequence] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [allowedClassIds, setAllowedClassIds] = useState<number[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ClassRow[]>([]);
  const [convoked, setConvoked] = useState<ConvokedStudent[]>([]);
  const [ranked, setRanked] = useState<RankedStudent[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [summary, setSummary] = useState<{ inscrits: number; evalues: number; admis: number; taux: string } | null>(null);
  const academicYearId = getYearId();
  const academicYearLabel = getYearLabel();

  // ─── Référentiels ───
  const loadRef = useCallback(async () => {
    try {
      const params: any = {};
      if (academicYearId) params.academic_year_id = academicYearId;
      const [levRes, perRes, seqRes] = await Promise.all([
        apiService.get('/levels', { params }).catch(() => []),
        apiService.get('/grades/periods', { params }).catch(() => []),
        apiService.get('/grades/sequences', { params }).catch(() => []),
      ]);
      setLevels(extractData(levRes));
      setPeriods(extractData(perRes));
      setSequences(extractData(seqRes));
    } catch { /* ignore */ }
  }, [academicYearId]);

  // Classes attribuées au censeur
  useEffect(() => {
    if (!isCenseur) { setAllowedClassIds(null); return; }
    apiService.get('/discipline/allowed-classes')
      .then((res: any) => setAllowedClassIds(extractData(res).map((c: any) => c.id).filter((id: any) => id != null)))
      .catch(() => setAllowedClassIds([]));
  }, [isCenseur]);

  useEffect(() => { if (canAccess) loadRef(); }, [canAccess, loadRef]);

  const seqPeriodMap = useMemo(() => {
    const m = new Map<number, number>();
    sequences.forEach((s: any) => { if (s.evaluation_period_id) m.set(Number(s.id), Number(s.evaluation_period_id)); });
    return m;
  }, [sequences]);

  const currentPeriod = useMemo(() => periods.find((p: any) => String(p.id) === selectedPeriod), [periods, selectedPeriod]);

  // ═══════════════════════════════════════════════════════════════
  // CALCUL DES STATISTIQUES
  // ═══════════════════════════════════════════════════════════════
  const compute = useCallback(async () => {
    if (!selectedLevel) { setRows([]); setConvoked([]); setRanked([]); setSuggestions([]); setSummary(null); return; }
    setLoading(true);
    try {
      // 1) Classes du niveau (censeur : restreint aux classes attribuées)
      const clsRes: any = await apiService.get('/classes', { params: { level_id: Number(selectedLevel), per_page: 1000 } }).catch(() => []);
      let classes = extractData(clsRes).filter((c: any) => c.level_id != null && Number(c.level_id) === Number(selectedLevel));
      if (isCenseur && allowedClassIds !== null) {
        classes = classes.filter((c: any) => allowedClassIds.includes(Number(c.id)));
      }

      // Bornes de période (pour les absences)
      let dateFrom: string | null = null, dateTo: string | null = null;
      if (mode === 'sequence') {
        const seq = sequences.find((s: any) => String(s.id) === selectedSequence);
        dateFrom = seq?.start_date || null; dateTo = seq?.end_date || null;
      } else if (mode === 'trimestre' && currentPeriod) {
        dateFrom = currentPeriod.start_date || null; dateTo = currentPeriod.end_date || null;
      }

      const allRows: ClassRow[] = [];
      const allConvoked: ConvokedStudent[] = [];
      const allRanked: RankedStudent[] = [];

      await Promise.all(classes.map(async (cls: any) => {
        const classId = Number(cls.id);
        // 2) Élèves inscrits — via /students?class_id (comme GradesPage) :
        // /classes/{id}/students n'existe pas dans ce backend → liste vide → 0 partout.
        const stuParams: any = { class_id: classId, active_only: true, per_page: 500 };
        if (academicYearId) stuParams.academic_year_id = academicYearId;
        let students = extractData(await apiService.get('/students', { params: stuParams }).catch(() => []));
        if (students.length === 0 && academicYearId) {
          const { academic_year_id, ...noYear } = stuParams;
          students = extractData(await apiService.get('/students', { params: noYear }).catch(() => []));
        }
        // 3) Notes (année complète, filtrées par scope côté client)
        const gParams: any = { class_id: classId };
        if (academicYearId) gParams.academic_year_id = academicYearId;
        let grades = extractData(await apiService.get('/grades', { params: gParams }).catch(() => []));
        if (grades.length === 0 && academicYearId) {
          const { academic_year_id, ...noYear } = gParams;
          grades = extractData(await apiService.get('/grades', { params: noYear }).catch(() => []));
        }
        const scoped = grades.filter((g: any) => {
          if (mode === 'sequence') return Number(g.sequence_id) === Number(selectedSequence);
          if (mode === 'trimestre') return seqPeriodMap.get(Number(g.sequence_id)) === Number(selectedPeriod);
          return true; // annuelle
        });
        // 4) Absences (heures par élève)
        const dParams: any = { class_id: classId, incident_type: 'ABSENCE', per_page: 1000 };
        if (dateFrom) dParams.date_from = String(dateFrom).slice(0, 10);
        if (dateTo) dParams.date_to = String(dateTo).slice(0, 10);
        const absences = extractData(await apiService.get('/discipline', { params: dParams }).catch(() => []));
        const absHoursByStudent = new Map<number, number>();
        absences.forEach((a: any) => {
          const sid = Number(a.student_id);
          absHoursByStudent.set(sid, (absHoursByStudent.get(sid) || 0) + getNum(a.duration_hours));
        });
        // 5) Moyennes par élève (pondérées par coefficient)
        const byStudent = new Map<number, { pts: number; coef: number; count: number }>();
        scoped.forEach((g: any) => {
          const sid = Number(g.student_id);
          const coef = getNum(g.coefficient) || 1;
          const v = getNum(g.value ?? g.score);
          const cur = byStudent.get(sid) || { pts: 0, coef: 0, count: 0 };
          cur.pts += v * coef; cur.coef += coef; cur.count += 1;
          byStudent.set(sid, cur);
        });
        const avgOf = (sid: number): number | null => {
          const s = byStudent.get(sid);
          return s && s.coef > 0 ? s.pts / s.coef : null;
        };

        const isActive = (s: any) => ['INSCRIT', 'NOUVEAU', 'active'].includes(String(s.status || '').toUpperCase());
        const activeStudents = students.filter(isActive);
        const studentSex = new Map<number, string>(students.map((s: any) => [Number(s.id), String(s.sex || s.gender || '').toUpperCase()]));

        const aggregate = (list: any[]) => {
          let inscrits = list.length, evalues = 0, admis = 0, red = 0, exc = 0, conv = 0;
          list.forEach((s: any) => {
            const sid = Number(s.id);
            if (mode === 'annuelle') {
              if (s.is_repeating) red++;
              if (String(s.status).toUpperCase() === 'EXCLU') exc++;
            }
            const avg = avgOf(sid);
            if (avg !== null) {
              evalues++;
              if (avg >= 10) admis++;
              if (mode !== 'annuelle') {
                const hrs = absHoursByStudent.get(sid) || 0;
                if (avg < 7.5 || hrs > 10) {
                  conv++;
                  allConvoked.push({
                    student_id: sid,
                    class_name: cls.name || `Classe ${classId}`,
                    name: `${s.last_name || ''} ${s.first_name || ''}`.trim() || (s.full_name || '—'),
                    matricule: s.matricule || '—',
                    moyenne: Math.round(avg * 100) / 100,
                    heures_absence: hrs,
                    parent_contact: s.emergency_phone || s.emergency_contact || '—',
                    motif: [avg < 7.5 ? `moyenne ${avg.toFixed(2)}/20` : '', hrs > 10 ? `${hrs}h d'absence` : ''].filter(Boolean).join(' + '),
                  });
                }
              }
            }
          });
          return { inscrits, evalues, admis, red, exc, conv };
        };

        // ── Élèves évalués (moyenne connue) → listes admis / échoués / top5 ──
        activeStudents.forEach((s: any) => {
          const avg = avgOf(Number(s.id));
          if (avg !== null) {
            allRanked.push({
              student_id: Number(s.id),
              class_name: cls.name || `Classe ${classId}`,
              name: `${s.last_name || ''} ${s.first_name || ''}`.trim() || (s.full_name || '—'),
              matricule: s.matricule || '—',
              sexe: String(s.sex || s.gender || '').toUpperCase() === 'F' ? 'F' : 'M',
              age: ageOf(s.date_of_birth),
              moyenne: Math.round(avg * 100) / 100,
              admis: avg >= 10,
            });
          }
        });

        const perSex = (sex: string) => aggregate(activeStudents.filter((s: any) => studentSex.get(Number(s.id)) === sex));
        const total = aggregate(activeStudents);
        const mk = (sexe: string, a: any): ClassRow => ({
          class_id: classId, class_name: cls.name || `Classe ${classId}`, sexe,
          inscrits: a.inscrits, evalues: a.evalues, admis: a.admis,
          redoublants: a.red, exclus: a.exc, convocations: a.conv,
          taux: fmtPct(a.admis, a.evalues),
        });
        allRows.push(mk('F', perSex('F')), mk('M', perSex('M')), mk('TOTAL', total));
      }));

      allRows.sort((a, b) => a.class_name.localeCompare(b.class_name, 'fr') || (a.sexe === 'TOTAL' ? 1 : b.sexe === 'TOTAL' ? -1 : a.sexe.localeCompare(b.sexe)));
      setRows(allRows);

      const totals = allRows.filter(r => r.sexe === 'TOTAL')
        .reduce((acc, r) => ({ inscrits: acc.inscrits + r.inscrits, evalues: acc.evalues + r.evalues, admis: acc.admis + r.admis }), { inscrits: 0, evalues: 0, admis: 0 });
      setSummary({ ...totals, taux: fmtPct(totals.admis, totals.evalues) });

      allConvoked.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
      setConvoked(allConvoked);

      allRanked.sort((a, b) => a.class_name.localeCompare(b.class_name, 'fr') || b.moyenne - a.moyenne);
      setRanked(allRanked);

      // ─── Suggestions annuelles ───
      if (mode === 'annuelle') {
        const sug: string[] = [];
        const tauxGlobal = totals.evalues > 0 ? (totals.admis / totals.evalues) * 100 : 0;
        const byClass = allRows.filter(r => r.sexe === 'TOTAL');
        const worst = [...byClass].sort((a, b) => parseFloat(a.taux) - parseFloat(b.taux))[0];
        const totalRed = byClass.reduce((s, r) => s + r.redoublants, 0);
        const totalExc = byClass.reduce((s, r) => s + r.exclus, 0);
        if (tauxGlobal < 50) sug.push(`Le taux de réussite global (${tauxGlobal.toFixed(1)}%) est faible : organiser un conseil pédagogique d'évaluation et renforcer le suivi des apprentissages.`);
        else if (tauxGlobal < 70) sug.push(`Taux de réussite global de ${tauxGlobal.toFixed(1)}% : envisager des séances de soutien ciblées dans les classes les plus faibles.`);
        else sug.push(`Taux de réussite global satisfaisant (${tauxGlobal.toFixed(1)}%) : maintenir les dispositifs de remédiation et féliciter les équipes performantes.`);
        if (worst && parseFloat(worst.taux) < 50) sug.push(`Classe prioritaire : ${worst.class_name} (taux de réussite ${worst.taux}) — mise en place d'un plan de renforcement en ${worst.class_name}.`);
        if (totalRed > 0) sug.push(`${totalRed} redoublant(s) : organiser des classes de rattrapage intensif en début d'année suivante.`);
        if (totalExc > 0) sug.push(`${totalExc} élève(s) exclu(s) : convoquer les parents et instruire les dossiers de réintégration conformément au règlement intérieur.`);
        sug.push(`Renforcer la coopération école-parents (visites, réunions de classes) pour améliorer l'assiduité et les résultats.`);
        setSuggestions(sug);
      } else {
        setSuggestions([]);
      }
    } catch (e) {
      console.error('[StatsNiveau]', e);
      toast.error('Erreur lors du calcul des statistiques');
    } finally {
      setLoading(false);
    }
  }, [selectedLevel, selectedSequence, selectedPeriod, mode, sequences, seqPeriodMap, currentPeriod, academicYearId, isCenseur, allowedClassIds]);

  useEffect(() => { if (canAccess) compute(); }, [compute, canAccess]);

  const admisList = useMemo(() => ranked.filter(r => r.admis), [ranked]);
  const groupedAdmis = useMemo(() => {
    const m = new Map<string, RankedStudent[]>();
    admisList.forEach(r => { if (!m.has(r.class_name)) m.set(r.class_name, []); m.get(r.class_name)!.push(r); });
    return m;
  }, [admisList]);
  const echouesList = useMemo(() => ranked.filter(r => !r.admis), [ranked]);
  const groupedEchoues = useMemo(() => {
    const m = new Map<string, RankedStudent[]>();
    echouesList.forEach(r => { if (!m.has(r.class_name)) m.set(r.class_name, []); m.get(r.class_name)!.push(r); });
    return m;
  }, [echouesList]);
  const topBottomByClass = useMemo(() => {
    const m = new Map<string, { premiers: RankedStudent[]; derniers: RankedStudent[] }>();
    const byClass = new Map<string, RankedStudent[]>();
    ranked.forEach(r => {
      if (!byClass.has(r.class_name)) byClass.set(r.class_name, []);
      byClass.get(r.class_name)!.push(r);
    });
    byClass.forEach((list, cls) => {
      const sorted = [...list].sort((a, b) => b.moyenne - a.moyenne);
      m.set(cls, { premiers: sorted.slice(0, 5), derniers: sorted.slice(-5).reverse() });
    });
    return m;
  }, [ranked]);

  // ═══════════════════════════════════════════════════════════════
  // IMPRESSION (statistiques + convocations + admis + échoués + top5)
  // ═══════════════════════════════════════════════════════════════
  const handlePrint = () => {
    if (rows.length === 0) { toast.warning('Générez d\'abord les statistiques'); return; }
    const levelName = levels.find((l: any) => String(l.id) === selectedLevel)?.name || levels.find((l: any) => String(l.id) === selectedLevel)?.label_fr || '—';
    let hdr: any = null;
    try { hdr = JSON.parse(localStorage.getItem('lybibal-school-header') || 'null'); } catch { hdr = null; }
    const H = hdr || {
      school_name: 'LYCÉE BILINGUE DE BALENG', school_name_en: 'GBHS BALENG',
      address: 'BP : 1041 Baleng', region: "RÉGION DE L'OUEST", department: 'DÉPARTEMENT DE LA MIFI',
    };
    const today = new Date().toLocaleDateString('fr-FR');
    let title = 'STATISTIQUES ANNUELLES';
    let subtitle = `Niveau : ${levelName} — Année scolaire : ${academicYearLabel || academicYearId || '—'}`;
    if (mode === 'trimestre') {
      const pNum = currentPeriod?.code?.match(/T(\d)/)?.[1] || currentPeriod?.order_index || '';
      title = `STATISTIQUES TRIMESTRE N°${pNum}`;
    } else if (mode === 'sequence') {
      const seq = sequences.find((s: any) => String(s.id) === selectedSequence);
      const num = seq?.order_index || (sequences.filter((s: any) => seqPeriodMap.get(Number(s.id)) === seq?.evaluation_period_id).findIndex((s: any) => String(s.id) === selectedSequence) + 1) || '';
      title = `STATISTIQUES SÉQUENCE N°${num}`;
      if (seq?.label) subtitle += ` — ${seq.label}`;
    }

    const isAnnual = mode === 'annuelle';
    const headers = isAnnual
      ? ['Classe', 'Sexe', 'Inscrits', 'Évalués', 'Admis', '% Réussite', 'Redoublants', 'Exclus']
      : ['Classe', 'Sexe', 'Inscrits', 'Évalués', 'Admis', '% Réussite', 'Observation'];
    const bodyRows = rows.map(r => {
      const isTotal = r.sexe === 'TOTAL';
      const obs = !isAnnual && isTotal ? (r.convocations > 0 ? `Convocation : ${r.convocations} élève(s)` : '—') : (!isAnnual ? '' : '');
      const cells = isAnnual
        ? [r.class_name, r.sexe, r.inscrits, r.evalues, r.admis, r.taux, r.redoublants, r.exclus]
        : [r.class_name, r.sexe, r.inscrits, r.evalues, r.admis, r.taux, obs];
      return `<tr style="${isTotal ? 'font-weight:bold;background:#eef2f7;' : ''}">
        ${cells.map((c, i) => `<td style="border:1px solid #555;padding:4px 6px;text-align:${i === 0 ? 'left' : 'center'};font-size:10px;">${c}</td>`).join('')}
      </tr>`;
    }).join('');

    const nbBlock = !isAnnual ? `<p style="margin-top:14px;font-size:11px;font-style:italic;"><strong>N.B :</strong> Convocation des parents lorsque la moyenne est inférieure à 7,5/20 et/ou le nombre d'heures d'absence est supérieur à 10.</p>` : '';
    const sugBlock = isAnnual && suggestions.length > 0 ? `
      <div style="margin-top:14px;">
        <p style="font-size:12px;font-weight:bold;text-decoration:underline;">Suggestions :</p>
        <ul style="margin:4px 0 0 18px;font-size:11px;line-height:1.6;">
          ${suggestions.map(s => `<li>${s}</li>`).join('')}
        </ul>
      </div>` : '';

    // Page 2 : convocations par classe avec contacts parents
    let convPage = '';
    if (!isAnnual && convoked.length > 0) {
      const grouped = (() => {
        const m = new Map<string, ConvokedStudent[]>();
        convoked.forEach(c => {
          if (!m.has(c.class_name)) m.set(c.class_name, []);
          m.get(c.class_name)!.push(c);
        });
        return m;
      })();
      convPage = `<div style="page-break-before:always;">
        <h2 style="text-align:center;font-size:16px;font-weight:bold;color:#1e3a5f;margin:0 0 4px;">LISTE DES ÉLÈVES CONVOQUÉS</h2>
        <p style="text-align:center;font-size:11px;color:#555;margin:0 0 14px;">${subtitle} — ${title}</p>
        ${Array.from(grouped.entries()).map(([cls, list]) => `
          <p style="font-size:12px;font-weight:bold;background:#1e3a5f;color:#fff;padding:4px 8px;margin:12px 0 0;">${cls} <span style="float:right;font-weight:normal;">${list.length} élève(s)</span></p>
          <table style="width:100%;border-collapse:collapse;">
            <thead><tr>
              <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">N°</th>
              <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Matricule</th>
              <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Nom et Prénom</th>
              <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Moyenne</th>
              <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">H. absence</th>
              <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Motif</th>
              <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Contact parent</th>
            </tr></thead>
            <tbody>${list.map((c, i) => `<tr>
              <td style="border:1px solid #555;padding:4px 6px;text-align:center;font-size:10px;">${i + 1}</td>
              <td style="border:1px solid #555;padding:4px 6px;text-align:center;font-size:10px;">${c.matricule}</td>
              <td style="border:1px solid #555;padding:4px 6px;font-size:10px;">${c.name}</td>
              <td style="border:1px solid #555;padding:4px 6px;text-align:center;font-size:10px;">${c.moyenne?.toFixed(2) ?? '—'}</td>
              <td style="border:1px solid #555;padding:4px 6px;text-align:center;font-size:10px;">${c.heures_absence}h</td>
              <td style="border:1px solid #555;padding:4px 6px;font-size:10px;">${c.motif}</td>
              <td style="border:1px solid #555;padding:4px 6px;text-align:center;font-size:10px;">${c.parent_contact}</td>
            </tr>`).join('')}</tbody>
          </table>`).join('')}
      </div>`;
    }

    // ─── Pages ADMIS / ÉCHOUÉS / 5 PREMIERS & 5 DERNIERS (tous modes) ───
    const groupedByClass = (list: RankedStudent[]) => {
      const m = new Map<string, RankedStudent[]>();
      list.forEach(r => { if (!m.has(r.class_name)) m.set(r.class_name, []); m.get(r.class_name)!.push(r); });
      return m;
    };
    const studentTable = (list: RankedStudent[], obsLabel: string) => `
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr>
          <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">N°</th>
          <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Matricule</th>
          <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Nom et Prénom</th>
          <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Sexe</th>
          <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Âge</th>
          <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Moyenne</th>
          <th style="border:1px solid #555;background:#2d4a6f;color:#fff;padding:4px 6px;font-size:10px;">Observation</th>
        </tr></thead>
        <tbody>${list.map((c, i) => `<tr>
          <td style="border:1px solid #555;padding:4px 6px;text-align:center;font-size:10px;">${i + 1}</td>
          <td style="border:1px solid #555;padding:4px 6px;text-align:center;font-size:10px;">${c.matricule}</td>
          <td style="border:1px solid #555;padding:4px 6px;font-size:10px;">${c.name}</td>
          <td style="border:1px solid #555;padding:4px 6px;text-align:center;font-size:10px;">${c.sexe}</td>
          <td style="border:1px solid #555;padding:4px 6px;text-align:center;font-size:10px;">${c.age ?? '—'}</td>
          <td style="border:1px solid #555;padding:4px 6px;text-align:center;font-size:10px;font-weight:bold;">${c.moyenne.toFixed(2)}</td>
          <td style="border:1px solid #555;padding:4px 6px;font-size:10px;">${obsLabel}</td>
        </tr>`).join('')}</tbody>
      </table>`;
    const classPages = (title: string, list: RankedStudent[], obsLabel: string, emptyMsg: string) => {
      if (list.length === 0) return '';
      const grouped = groupedByClass(list);
      return `<div style="page-break-before:always;">
        <h2 style="text-align:center;font-size:16px;font-weight:bold;color:#1e3a5f;margin:0 0 4px;">${title}</h2>
        <p style="text-align:center;font-size:11px;color:#555;margin:0 0 14px;">${subtitle} — ${levelName}</p>
        ${Array.from(grouped.entries()).map(([cls, students]) => `
          <p style="font-size:12px;font-weight:bold;background:#1e3a5f;color:#fff;padding:4px 8px;margin:12px 0 0;">${cls} <span style="float:right;font-weight:normal;">${students.length} élève(s)</span></p>
          ${studentTable(students, obsLabel)}`).join('')}
        <p style="margin-top:12px;font-size:10px;font-style:italic;color:#555;">${emptyMsg}</p>
      </div>`;
    };
    const admisPage = classPages('LISTE DES ADMIS', admisList, 'Admis(e)', '');
    const echouesPage = classPages('LISTE DES ÉCHOUÉS', echouesList, 'Échoué(e) — à renforcer', "N.B : les élèves échoués avec une moyenne inférieure à 7,5 et/ou plus de 10h d'absence font l'objet d'une convocation des parents.");
    const topPage = (() => {
      if (ranked.length === 0) return '';
      return `<div style="page-break-before:always;">
        <h2 style="text-align:center;font-size:16px;font-weight:bold;color:#1e3a5f;margin:0 0 4px;">LES 5 PREMIERS ET LES 5 DERNIERS</h2>
        <p style="text-align:center;font-size:11px;color:#555;margin:0 0 14px;">${subtitle} — ${levelName}</p>
        ${Array.from(topBottomByClass.entries()).map(([cls, tb]) => `
          <p style="font-size:12px;font-weight:bold;background:#1e3a5f;color:#fff;padding:4px 8px;margin:12px 0 0;">${cls}</p>
          <p style="font-size:11px;font-weight:bold;margin:8px 0 2px;color:#15803d;">5 premiers</p>
          ${studentTable(tb.premiers, 'Premier(s)')}
          <p style="font-size:11px;font-weight:bold;margin:10px 0 2px;color:#b91c1c;">5 derniers</p>
          ${studentTable(tb.derniers, 'Dernier(s) — à encadrer')}`).join('')}
      </div>`;
    })();

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
      <style>@page{size:A4 portrait;margin:12mm}body{font-family:Arial,sans-serif;margin:0;font-size:11px}
      *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
      table{width:100%;border-collapse:collapse}thead{display:table-header-group}tr{page-break-inside:avoid}</style>
    </head><body>
      <div style="text-align:center;border-bottom:2px solid #1e3a5f;padding-bottom:8px;margin-bottom:12px;">
        <p style="margin:0;font-size:11px;font-weight:bold;color:#1e3a5f;">RÉPUBLIQUE DU CAMEROUN</p>
        <p style="margin:2px 0;font-size:9px;color:#555;">Paix-Travail-Patrie — ${H.region} — ${H.department}</p>
        <p style="margin:6px 0 0;font-size:15px;font-weight:bold;color:#1e3a5f;">${H.school_name}</p>
        <p style="margin:2px 0;font-size:11px;color:#1e3a5f;">${H.school_name_en}</p>
        <p style="margin:2px 0;font-size:9px;color:#666;">${H.address || ''}</p>
      </div>
      <h1 style="text-align:center;font-size:22px;font-weight:bold;letter-spacing:1px;margin:14px 0 4px;text-transform:uppercase;">${title}</h1>
      <p style="text-align:center;font-size:11px;color:#555;margin:0 0 14px;">${subtitle}</p>
      <table>
        <thead><tr>${headers.map(h => `<th style="border:1px solid #555;background:#1e3a5f;color:#fff;padding:5px 6px;font-size:10px;text-align:center;">${h}</th>`).join('')}</tr></thead>
        <tbody>${bodyRows}</tbody>
      </table>
      ${summary ? `<p style="margin-top:10px;font-size:11px;"><strong>Totaux :</strong> Inscrits ${summary.inscrits} — Évalués ${summary.evalues} — Admis ${summary.admis} — Taux de réussite ${summary.taux}</p>` : ''}
      ${nbBlock}
      ${sugBlock}
      <div style="margin-top:26px;display:flex;justify-content:space-between;font-size:11px;">
        <span>Fait à Baleng, le ${today}</span>
        <span style="text-align:center;">Le Censeur<br/><br/><br/>____________________</span>
        <span style="text-align:center;">Le Proviseur<br/><br/><br/>____________________</span>
      </div>
      <p style="margin-top:18px;text-align:center;font-size:8px;color:#999;border-top:1px solid #eee;padding-top:6px;">myhelperschool.cm — https://myhelperschool.cm/</p>
      ${convPage}
      ${admisPage}
      ${echouesPage}
      ${topPage}
    </body></html>`;
    const pw = window.open('', '_blank');
    if (!pw) { toast.error('Autorisez les popups'); return; }
    pw.document.write(html);
    pw.document.close();
    pw.focus();
    pw.print();
  };

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
          <h2 className="text-lg font-bold text-gray-900">Accès restreint</h2>
          <p className="text-sm text-gray-500 mt-1">Cette page est réservée au super admin, à l'admin et au censeur.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-7 h-7 text-[#1e3a8a]" />
          Statistiques par niveau
        </h1>
        <p className="text-sm text-gray-500 mt-1">Réussite par classe et par sexe — séquence, trimestre et année {academicYearLabel && <span className="text-blue-600 font-medium">— {academicYearLabel}</span>}</p>
      </div>

      {/* Contrôles */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {([['sequence', 'Séquence'], ['trimestre', 'Trimestre'], ['annuelle', 'Annuelle']] as const).map(([m, lbl]) => (
              <button key={m} onClick={() => setMode(m)}
                className={`px-4 py-2 text-xs font-medium transition-colors ${mode === m ? 'bg-[#1e3a8a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Niveau *</label>
          <select value={selectedLevel} onChange={(e) => setSelectedLevel(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[180px]">
            <option value="">Sélectionner…</option>
            {levels.map((l: any) => <option key={l.id} value={l.id}>{l.name || l.label_fr || `Niveau ${l.id}`}</option>)}
          </select>
        </div>
        {mode === 'sequence' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Séquence *</label>
            <select value={selectedSequence} onChange={(e) => setSelectedSequence(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[200px]">
              <option value="">Sélectionner…</option>
              {periods.map((p: any) => (
                <optgroup key={p.id} label={p.label_fr || p.code}>
                  {sequences.filter((s: any) => s.evaluation_period_id === p.id && s.is_active !== false).map((s: any) => (
                    <option key={s.id} value={s.id}>{s.label || s.code}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        )}
        {mode === 'trimestre' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Trimestre *</label>
            <select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[180px]">
              <option value="">Sélectionner…</option>
              {periods.map((p: any) => <option key={p.id} value={p.id}>{p.label_fr || p.code}</option>)}
            </select>
          </div>
        )}
        <button onClick={compute} disabled={loading || !selectedLevel || (mode === 'sequence' && !selectedSequence) || (mode === 'trimestre' && !selectedPeriod)}
          className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] disabled:opacity-50 flex items-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? 'Calcul…' : 'Générer'}
        </button>
        <button onClick={handlePrint} disabled={rows.length === 0}
          className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 disabled:opacity-50 flex items-center gap-2 ml-auto">
          <Printer className="w-4 h-4" /> Imprimer (listes incluses)
        </button>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1e3a5f] text-white">
              <tr>
                {(mode === 'annuelle'
                  ? ['Classe', 'Sexe', 'Inscrits', 'Évalués', 'Admis', '% Réussite', 'Redoublants', 'Exclus']
                  : ['Classe', 'Sexe', 'Inscrits', 'Évalués', 'Admis', '% Réussite', 'Observation']
                ).map(h => <th key={h} className="px-3 py-2.5 text-center text-xs font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={8} className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /><p className="text-sm text-gray-500 mt-2">Calcul en cours…</p></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center text-gray-400"><BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />Sélectionnez un niveau puis cliquez sur « Générer »</td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} className={r.sexe === 'TOTAL' ? 'bg-gray-50 font-bold' : 'hover:bg-gray-50'}>
                  <td className="px-3 py-2 text-sm">{r.sexe === 'F' ? r.class_name : r.sexe === 'TOTAL' ? `${r.class_name} — Total` : ''}</td>
                  <td className="px-3 py-2 text-center text-sm">{r.sexe}</td>
                  <td className="px-3 py-2 text-center text-sm">{r.inscrits}</td>
                  <td className="px-3 py-2 text-center text-sm">{r.evalues}</td>
                  <td className="px-3 py-2 text-center text-sm text-green-700 font-semibold">{r.admis}</td>
                  <td className="px-3 py-2 text-center text-sm">{r.taux}</td>
                  {mode === 'annuelle' ? (
                    <>
                      <td className="px-3 py-2 text-center text-sm text-amber-700">{r.redoublants}</td>
                      <td className="px-3 py-2 text-center text-sm text-red-700">{r.exclus}</td>
                    </>
                  ) : (
                    <td className="px-3 py-2 text-center text-xs">{r.sexe === 'TOTAL' ? (r.convocations > 0 ? <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">Convocation : {r.convocations}</span> : '—') : ''}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {summary && rows.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-sm flex flex-wrap gap-x-6 gap-y-1">
            <span><strong>Inscrits :</strong> {summary.inscrits}</span>
            <span><strong>Évalués :</strong> {summary.evalues}</span>
            <span><strong>Admis :</strong> {summary.admis}</span>
            <span><strong>Taux de réussite :</strong> {summary.taux}</span>
          </div>
        )}
      </div>

      {/* N.B + Convocations */}
      {mode !== 'annuelle' && rows.length > 0 && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <strong>N.B :</strong> Convocation des parents lorsque la moyenne est inférieure à 7,5/20 et/ou le nombre d'heures d'absence est supérieur à 10.
          {convoked.length > 0 && <span className="block mt-1 font-medium">{convoked.length} élève(s) à convoquer — voir page 2 de l'impression.</span>}
        </div>
      )}

      {/* Suggestions annuelles */}
      {mode === 'annuelle' && suggestions.length > 0 && (
        <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#1e3a8a]" />Suggestions</h3>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            {suggestions.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      {/* ─── LISTES : Admis / Échoués / 5 premiers & 5 derniers ─── */}
      {ranked.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          {/* ADMIS */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-green-50 border-b border-green-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-green-800">Liste des admis</h3>
              <span className="px-2 py-0.5 bg-green-600 text-white rounded-full text-xs font-bold">{admisList.length}</span>
            </div>
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
              {Array.from(groupedAdmis.entries()).map(([cls, list]) => (
                <div key={cls}>
                  <p className="px-4 py-1.5 bg-gray-50 text-xs font-bold text-gray-600 sticky top-0">{cls} ({list.length})</p>
                  {list.map((r, i) => (
                    <div key={r.student_id} className="px-4 py-1.5 flex items-center justify-between text-sm">
                      <span className="truncate"><span className="text-gray-400 mr-1">{i + 1}.</span> <span className="font-mono text-xs text-gray-500">{r.matricule}</span> {r.name}</span>
                      <span className="font-bold text-green-700 shrink-0 ml-2">{r.moyenne.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          {/* ÉCHOUÉS */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-red-50 border-b border-red-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-red-800">Liste des échoués</h3>
              <span className="px-2 py-0.5 bg-red-600 text-white rounded-full text-xs font-bold">{echouesList.length}</span>
            </div>
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
              {Array.from(groupedEchoues.entries()).map(([cls, list]) => (
                <div key={cls}>
                  <p className="px-4 py-1.5 bg-gray-50 text-xs font-bold text-gray-600 sticky top-0">{cls} ({list.length})</p>
                  {list.map((r, i) => (
                    <div key={r.student_id} className="px-4 py-1.5 flex items-center justify-between text-sm">
                      <span className="truncate"><span className="text-gray-400 mr-1">{i + 1}.</span> <span className="font-mono text-xs text-gray-500">{r.matricule}</span> {r.name}</span>
                      <span className="font-bold text-red-700 shrink-0 ml-2">{r.moyenne.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          {/* 5 PREMIERS / 5 DERNIERS */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-blue-800">5 premiers / 5 derniers</h3>
              <span className="px-2 py-0.5 bg-blue-600 text-white rounded-full text-xs font-bold">{topBottomByClass.size} classe(s)</span>
            </div>
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
              {Array.from(topBottomByClass.entries()).map(([cls, tb]) => (
                <div key={cls} className="px-4 py-2">
                  <p className="text-xs font-bold text-gray-700">{cls}</p>
                  <p className="text-[10px] font-bold text-green-700 mt-1">Premiers</p>
                  {tb.premiers.map((r, i) => (
                    <div key={r.student_id} className="flex items-center justify-between text-xs py-0.5">
                      <span className="truncate">{i + 1}. {r.name}</span>
                      <span className="font-bold text-green-700 ml-2">{r.moyenne.toFixed(2)}</span>
                    </div>
                  ))}
                  <p className="text-[10px] font-bold text-red-700 mt-1">Derniers</p>
                  {tb.derniers.map((r, i) => (
                    <div key={r.student_id} className="flex items-center justify-between text-xs py-0.5">
                      <span className="truncate">{i + 1}. {r.name}</span>
                      <span className="font-bold text-red-700 ml-2">{r.moyenne.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}